import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, MessageSquare, FileCheck, CreditCard, CheckCircle, AlertCircle, ArrowRight, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';

const NotificationBell = ({ userType, apiEndpoint, title = 'Notificaciones' }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const fetchNotifications = async () => {
    try {
      const response = await api.get(apiEndpoint);
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // Poll every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [apiEndpoint]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = async (notification) => {
    // Mark as read
    if (!notification.read) {
      try {
        await api.put(`${apiEndpoint}/${notification.id}/read`);
        fetchNotifications();
      } catch (e) {}
    }
    
    if (notification.link) {
      navigate(notification.link);
    }
    setIsOpen(false);
  };

  const markAllAsRead = async () => {
    try {
      await api.put(`${apiEndpoint}/mark-all-read`);
      fetchNotifications();
    } catch (e) {}
  };

  const getIcon = (type) => {
    switch (type) {
      case 'quote_received':
      case 'quote_response':
        return MessageSquare;
      case 'contract_created':
      case 'contract_completed':
        return FileCheck;
      case 'payment_received':
      case 'payment_pending':
        return CreditCard;
      case 'quote_accepted':
        return CheckCircle;
      case 'strike_warning':
        return AlertCircle;
      default:
        return Bell;
    }
  };

  const getIconColor = (type) => {
    switch (type) {
      case 'quote_received':
      case 'quote_response':
        return 'bg-blue-500/10 border-blue-500/20 text-blue-400';
      case 'contract_created':
      case 'contract_completed':
        return 'bg-green-500/10 border-green-500/20 text-green-400';
      case 'payment_received':
        return 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400';
      case 'payment_pending':
        return 'bg-yellow-500/10 border-yellow-500/20 text-yellow-400';
      case 'quote_accepted':
        return 'bg-purple-500/10 border-purple-500/20 text-purple-400';
      case 'strike_warning':
        return 'bg-red-500/10 border-red-500/20 text-red-400';
      default:
        return 'bg-zinc-500/10 border-zinc-500/20 text-zinc-400';
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-zinc-800 transition-colors"
        whileTap={{ scale: 0.95 }}
        data-testid={`${userType}-notification-bell`}
      >
        <Bell size={20} className="text-zinc-400 hover:text-white transition-colors" />
        
        {/* Badge */}
        <AnimatePresence>
          {unreadCount > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-80 sm:w-96 bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl overflow-hidden z-50"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-700 bg-zinc-800/50">
              <h3 className="text-sm font-bold text-white">{title}</h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <>
                    <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs font-bold rounded-full">
                      {unreadCount} nueva{unreadCount > 1 ? 's' : ''}
                    </span>
                    <button
                      onClick={markAllAsRead}
                      className="text-xs text-blue-400 hover:text-blue-300"
                    >
                      Marcar leídas
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Notifications List */}
            <div className="max-h-96 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <Bell size={24} className="mx-auto text-zinc-600 mb-2" />
                  <p className="text-zinc-500 text-sm">Sin notificaciones</p>
                  <p className="text-zinc-600 text-xs">Te avisaremos cuando haya novedades</p>
                </div>
              ) : (
                notifications.map((notification, index) => {
                  const Icon = getIcon(notification.type);
                  const iconColors = getIconColor(notification.type);
                  return (
                    <motion.button
                      key={notification.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.03 }}
                      onClick={() => handleNotificationClick(notification)}
                      className={`w-full px-4 py-3 flex items-start gap-3 hover:bg-zinc-800 transition-colors text-left border-b border-zinc-800 last:border-0 group ${
                        !notification.read ? 'bg-blue-500/5' : ''
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-lg border flex items-center justify-center flex-shrink-0 ${iconColors}`}>
                        <Icon size={18} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm font-medium ${notification.read ? 'text-zinc-300' : 'text-white'}`}>
                            {notification.title}
                          </p>
                          <span className="text-xs text-zinc-500 flex-shrink-0 flex items-center gap-1">
                            <Clock size={10} />
                            {formatTime(notification.created_at)}
                          </span>
                        </div>
                        <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{notification.message}</p>
                        {!notification.read && (
                          <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mt-1" />
                        )}
                      </div>
                      <ArrowRight size={14} className="text-zinc-600 group-hover:text-zinc-400 transition-colors flex-shrink-0 mt-1" />
                    </motion.button>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;
